/**
 * Shared Type Definitions for Kostolany Egg Web Application
 * Strictly aligned with Python D1 Marts and JSON Schema contracts.
 */

export const EGG_PHASES = ['B3', 'A1', 'A2', 'A3', 'B1', 'B2'] as const;
export type EggPhase = (typeof EGG_PHASES)[number];

export const EGG_ZONES = ['BUY', 'WAIT', 'SELL'] as const;
export type EggZone = (typeof EGG_ZONES)[number];

export const MACRO_REGIMES = ['BULL_ZONE', 'TOP_ZONE', 'BEAR_ZONE'] as const;
export type MacroRegime = (typeof MACRO_REGIMES)[number];

export const STOP_CHOICES = ['atr', 'base_low', 'manual'] as const;
export type StopChoice = (typeof STOP_CHOICES)[number];

export const SIZING_CONSTRAINTS = [
  'NONE',
  'RISK',
  'WEIGHT',
  'CAP_HEADROOM',
  'ZERO_MULTIPLIER',
] as const;
export type SizingConstraint = (typeof SIZING_CONSTRAINTS)[number];

export const MIGRATION_BUCKETS = [
  'CONTRARIAN_BUY',
  'IGNITION_BREAKOUT',
  'OVERHEATING_ALERT',
  'BREAKDOWN_WARNING',
] as const;
export type MigrationBucket = (typeof MIGRATION_BUCKETS)[number];

/** Macro gate summary matching fct_macro_gate_daily */
export interface MacroGateSummary {
  trade_date: string;
  macro_regime: MacroRegime;
  macro_phase: EggPhase;
  pct_buy_zone: number;
  max_equity_cap: number;
  macro_sizing_multiplier: number;
  basis_points?: number | null;
  basis_pct?: number | null;
  is_futures_panic?: boolean;
  is_futures_euphoria?: boolean;
}

/** Breadth summary matching fct_market_breadth_daily */
export interface BreadthSummary {
  trade_date: string;
  total_stocks: number;
  pct_buy_zone: number;
  pct_wait_zone: number;
  pct_sell_zone: number;
  phase_counts: Record<EggPhase, number>;
  phase_pcts: Record<EggPhase, number>;
}

/** Single stock scanner record matching fct_daily_scanner */
export interface ScannerRecord {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  phase: EggPhase;
  macro_phase: EggPhase;
  resonance_signal: string;
  matrix_multiplier: number;
  macro_sizing_multiplier: number;
  migration_bucket: MigrationBucket | null;
  is_disposition: boolean;
  is_attention: boolean;
  is_limit_locked: boolean;
  ladder_position_pct: number | null;
  cpr: number | null;
  dog_ma200_bias: number | null;
  vr60?: number | null;
  rs20: number | null;
  rs60: number | null;
  shares: number;
  lots: number;
  odd_shares: number;
  ntd_at_risk: number;
  position_cost: number;
  constraint: SizingConstraint;
  atr14?: number | null;
  low_20d?: number | null;
  ladder_minus1_pullback?: number | null;
  ladder_4_base?: number | null;
  ladder_5_euphoria?: number | null;
}

/** 4G Position Sizing input parameters */
export interface PositionSizingParams {
  portfolio_equity: number;
  entry_price: number;
  stop_loss_price?: number;
  stop_choice?: StopChoice;
  atr14?: number | null;
  atr_multiplier?: number;
  base_low?: number | null;
  macro_multiplier?: number;
  max_equity_cap?: number;
  is_etf?: boolean;
}

/** 4G Position Sizing output result */
export interface SizingResult {
  shares: number;
  lots: number;
  odd_shares: number;
  entry_price: number;
  stop_loss_price: number;
  per_share_loss: number;
  max_allowed_loss: number;
  ntd_at_risk: number;
  position_cost: number;
  position_value: number;
  position_weight: number;
  constraint: SizingConstraint;
  is_valid: boolean;
  rejection_reason?: string | null;
}

/** Model parameters for What-If live simulation */
export interface ModelParameters {
  ma_short: number;
  ma_mid: number;
  ma_long: number;
  vma_short: number;
  vma_mid: number;
  atr_period: number;
  cpr_min: number;
  cpr_max: number;
  dog_pullback_min: number;
  dog_pullback_max: number;
  dog_breakout_min: number;
  dog_breakout_max: number;
  dog_euphoria_min: number;
  dog_climax_top: number;
  dog_markdown_mid: number;
  dog_panic_bottom: number;
  vr_volume_dry: number;
  vr_volume_surge: number;
  vr_climax_heavy: number;
  macro_bull_zone_min: number;
  macro_bear_zone_max: number;
  macro_futures_panic_basis: number;
  macro_futures_euphoria_basis: number;
}

export type PhaseZone = 'buy' | 'wait' | 'sell';

export interface PhaseMeta {
  phase: EggPhase;
  label: string;
  zone: PhaseZone;
  hint: string;
}

export const PHASE_META: Record<EggPhase, PhaseMeta> = {
  B3: { phase: 'B3', label: 'B3 蕭條末期 (逆勢買進)', zone: 'buy', hint: '恐慌拋售沉底，左側建倉布局' },
  A1: { phase: 'A1', label: 'A1 復甦初期 (底部吸籌)', zone: 'buy', hint: '窒息量收斂築底，均線翻揚初升' },
  A2: { phase: 'A2', label: 'A2 成長中段 (順勢推升)', zone: 'wait', hint: '多頭主升段，沿趨勢均線順勢續抱' },
  A3: { phase: 'A3', label: 'A3 繁榮末段 (過熱賣出)', zone: 'sell', hint: '爆天量正乖離過熱，分批獲利了結' },
  B1: { phase: 'B1', label: 'B1 衰退初期 (弱勢賣出)', zone: 'sell', hint: '高檔量縮轉弱跌破均線，避險減碼' },
  B2: { phase: 'B2', label: 'B2 衰退中段 (順勢觀望)', zone: 'wait', hint: '主跌段空頭排列，保留資金場外觀望' },
};

export const ZONE_PHASES: Record<PhaseZone, readonly EggPhase[]> = {
  buy: ['B3', 'A1'],
  wait: ['A2', 'B2'],
  sell: ['A3', 'B1'],
} as const;

export const ZONE_META: Record<PhaseZone, { label: string; description: string }> = {
  buy: { label: '買進區 (Buy)', description: '左側布局，逆勢承接與築底吸籌' },
  wait: { label: '觀察區 (Wait)', description: '順勢加碼持股或空頭防守觀望' },
  sell: { label: '賣出區 (Sell)', description: '過熱出貨分批獲利與破線減碼' },
};

/** Sector breadth item capturing phase distribution across an industry sector */
export interface SectorBreadthItem {
  sector: string;
  total_stocks: number;
  phase_counts: Record<EggPhase, number>;
  phase_pcts: Record<EggPhase, number>;
  dominant_phase: EggPhase;
  pct_buy_zone: number;
  pct_wait_zone: number;
  pct_sell_zone: number;
}

/** Click payload from heatmap cell -> StockScanner filter */
export interface SectorPhaseFilter {
  sector: string;
  phase: EggPhase;
}

/**
 * Pure aggregation function deriving SectorBreadthItem[] from a list of ScannerRecord.
 * Enforces zero-drift zone rounding (buy + wait + sell === 100.0%) and canonical tie-breaking.
 */
export function aggregateSectorBreadth(records: ScannerRecord[]): SectorBreadthItem[] {
  const sectorGroups: Record<string, ScannerRecord[]> = {};

  for (const r of records) {
    const sec = (r.sector || '其他業').trim();
    if (!sectorGroups[sec]) {
      sectorGroups[sec] = [];
    }
    sectorGroups[sec].push(r);
  }

  const items: SectorBreadthItem[] = [];

  for (const [sector, secRecords] of Object.entries(sectorGroups)) {
    const counts: Record<EggPhase, number> = {
      B3: 0,
      A1: 0,
      A2: 0,
      A3: 0,
      B1: 0,
      B2: 0,
    };

    for (const r of secRecords) {
      if (counts[r.phase] !== undefined) {
        counts[r.phase] += 1;
      }
    }

    const total = secRecords.length;
    const pcts: Record<EggPhase, number> = {
      B3: 0,
      A1: 0,
      A2: 0,
      A3: 0,
      B1: 0,
      B2: 0,
    };

    for (const p of EGG_PHASES) {
      pcts[p] = total > 0 ? Math.round((counts[p] / total) * 1000) / 10 : 0;
    }

    // Dominant phase: max count with deterministic tie-breaking in EGG_PHASES order
    let dominant: EggPhase = 'B3';
    let maxC = -1;
    for (const p of EGG_PHASES) {
      if (counts[p] > maxC) {
        maxC = counts[p];
        dominant = p;
      }
    }

    const buyCount = counts.B3 + counts.A1;
    const waitCount = counts.A2 + counts.B2;

    const buyPct = total > 0 ? Math.round((buyCount / total) * 1000) / 10 : 0;
    const waitPct = total > 0 ? Math.round((waitCount / total) * 1000) / 10 : 0;
    // Derive sellPct to guarantee sum equals 100.0%
    const sellPct = total > 0 ? Math.round((100.0 - buyPct - waitPct) * 10) / 10 : 0;

    items.push({
      sector,
      total_stocks: total,
      phase_counts: counts,
      phase_pcts: pcts,
      dominant_phase: dominant,
      pct_buy_zone: buyPct,
      pct_wait_zone: waitPct,
      pct_sell_zone: sellPct,
    });
  }

  // Default sort by total stocks descending
  return items.sort((a, b) => b.total_stocks - a.total_stocks || a.sector.localeCompare(b.sector, 'zh-Hant'));
}

export interface SectorBreadthResponse {
  as_of_date: string;
  total_sectors: number;
  total_stocks: number;
  items: SectorBreadthItem[];
  generated_at?: string;
}

